import React, { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import { convertFactor } from './unitConverter';

// Common units grouped by family. conversionFactor is USER-defined per
// ingredient (buy in liters → use in ml); we only SUGGEST factors for
// well-known unit pairs.
const UNIT_FAMILIES = {
  weight: ['g', 'kg', 'oz', 'lb'],
  volume: ['ml', 'l', 'fl oz', 'gal'],
  count: ['unit', 'dozen', 'case', 'pack'],
};
// Real converter (unitConverter.js): any pair inside the same family
// (weight g-base / volume ml-base / count unit-base) converts both ways —
// lb→g, gal→ml, oz→g… Custom units (case/pack) stay user-defined.
const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

/**
 * Ingredients / Raw Materials (Inventory module — opt-in).
 * Costing: weighted average per purchase (recommended method from the spec).
 * Stock lives per location (stockByLocation map) — the receipt's location
 * gets the units. Reorder point + par level drive the alerts.
 */
const IngredientsPage = () => {
  const [ingredients, setIngredients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPurchase, setShowPurchase] = useState(null); // ingredient receiving stock
  const user = getAuth().currentUser;

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const db = getFirestore();
      const [ingSnap, locSnap] = await Promise.all([
        getDocs(collection(db, 'users', user.uid, 'ingredients')),
        getDocs(collection(db, 'users', user.uid, 'locations')),
      ]);
      setIngredients(ingSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLocations(locSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stockOf = (ing) => Object.values(ing.stockByLocation || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const fmtQty = (v) => { const n = Number(v); return Number.isFinite(n) ? String(Math.round(n * 10000) / 10000) : '0'; };
  const stockFor = (ing, locId) => Number(ing.stockByLocation?.[locId] || 0);

  const statusOf = (ing) => {
    const total = stockOf(ing);
    if (total <= 0) return { label: 'Out', cls: 'danger' };
    if (ing.reorderPoint != null && total <= ing.reorderPoint) return { label: 'Low', cls: 'warning' };
    return { label: 'OK', cls: 'success' };
  };

  // Which products use this ingredient, and how much per product (the BOM).
  const findUsage = async (ing) => {
    const db = getFirestore();
    const catsSnap = await getDocs(collection(db, 'users', user.uid, 'categories'));
    const usage = [];
    for (const cat of catsSnap.docs) {
      const prodSnap = await getDocs(collection(db, 'users', user.uid, 'categories', cat.id, 'products'));
      for (const pdoc of prodSnap.docs) {
        const rows = (pdoc.data().recipe || []).filter((r) => r.ingredientId === ing.id);
        for (const r of rows) {
          usage.push({
            productId: pdoc.id,
            categoryId: cat.id,
            name: pdoc.data().name || pdoc.id,
            category: cat.data().name || '',
            qty: parseFloat(r.qty) || 0,
          });
        }
      }
    }
    return usage;
  };

  const handleUsedIn = async (ing) => {
    Swal.fire({ title: 'Scanning recipes…', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    const usage = await findUsage(ing);
    if (usage.length === 0) {
      Swal.fire({ title: `Used In — ${ing.name}`, html: '<p class="text-muted m-0">Not used in any product recipe.</p>', icon: 'info' });
      return;
    }
    const rows = usage.map((u, idx) => `
      <tr>
        <td style="text-align:left;padding:6px 4px;border-bottom:1px solid #eee;"><a href="/admin/products?category=${u.categoryId}&product=${u.productId}" target="_blank" rel="noopener" title="Open in new tab" style="color:#ff6b35;font-weight:700;">${u.name} <i class="fas fa-external-link-alt" style="font-size:10px;"></i></a><br/><small class="text-muted">${u.category}</small></td>
        <td style="text-align:right;padding:6px 4px;border-bottom:1px solid #eee;white-space:nowrap;"><strong>${fmtQty(u.qty)}</strong> ${ing.useUnit || 'unit'}</td>
      </tr>`).join('');
    Swal.fire({
      title: `Used In — ${ing.name}`,
      html: `<table style="width:100%;border-collapse:collapse;">${rows}</table>
             <p style="margin-top:10px;font-size:12px;color:#888;">Quantity used per 1 unit sold of each product.</p>`,
      width: 520,
    });
  };

  const handleDelete = async (ing) => {
    // Guard: an ingredient used in a product's recipe can't be deleted
    // (the BOM would silently break + depletion would stop finding stock).
    try {
      const usage = await findUsage(ing);
      const usedBy = usage.map((u) => u.name);
      if (usedBy.length > 0) {
        Swal.fire({
          title: 'Cannot delete — used in recipes',
          html: `This ingredient is in the recipe of: <strong>${usedBy.join(', ')}</strong>.<br/>Remove it from those products first.`,
          icon: 'error',
        });
        return;
      }
    } catch (e) {
      console.warn('[Ingredients] usage check failed', e);
    }

    const res = await Swal.fire({
      title: `Delete "${ing.name}"?`,
      text: 'It is not used in any product recipe.',
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
    });
    if (!res.isConfirmed) return;
    const db = getFirestore();
    await deleteDoc(doc(db, 'users', user.uid, 'ingredients', ing.id));
    await load();
  };

  return (
    <AdminLayout title="Ingredients">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0 font-weight-bold">Raw Materials / Ingredients</h5>
          <small className="text-muted">Stock per location · weighted-average costing · reorder alerts</small>
        </div>
        <button className="btn btn-primary btn-sm" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={() => { setEditing(null); setShowForm(true); }}>
          <i className="fas fa-plus mr-1"></i> New ingredient
        </button>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>Ingredient</th>
                <th>Use unit</th>
                <th className="text-end">Cost / use unit</th>
                {locations.map(l => <th key={l.id} className="text-end">{l.companyName || l.locationName}</th>)}
                <th className="text-end">Total stock</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6 + locations.length} className="text-center py-4"><div className="spinner-border spinner-border-sm" style={{ color: '#ff6b35' }} /></td></tr>
              ) : ingredients.length === 0 ? (
                <tr><td colSpan={6 + locations.length} className="text-center py-4 text-muted">
                  No raw materials yet — create the first one (then add stock with "Receive").
                </td></tr>
              ) : ingredients.map(ing => {
                const st = statusOf(ing);
                return (
                  <tr key={ing.id}>
                    <td>
                      <div className="fw-bold">{ing.name}</div>
                      {ing.reorderPoint != null && <small className="text-muted">reorder ≤ {ing.reorderPoint} {ing.useUnit}{ing.parLevel != null ? ` · par ${ing.parLevel}` : ''}</small>}
                    </td>
                    <td><span className="badge badge-light border">{ing.useUnit || 'unit'}</span></td>
                    <td className="text-end">{ing.costPerUseUnit ? money(ing.costPerUseUnit) : '—'}</td>
                    {locations.map(l => (
                      <td key={l.id} className="text-end">{stockFor(ing, l.id) ? fmtQty(stockFor(ing, l.id)) : '—'}</td>
                    ))}
                    <td className="text-end fw-bold">{fmtQty(stockOf(ing))} {ing.useUnit}</td>
                    <td><span className={`badge badge-${st.cls}`}>{st.label}</span></td>
                    <td className="text-end text-nowrap">
                      <button className="btn btn-sm btn-outline-success mr-1" title="Receive stock (purchase)" onClick={() => setShowPurchase(ing)}>
                        <i className="fas fa-truck-loading"></i> Receive
                      </button>
                      <button className="btn btn-sm btn-outline-info mr-1" title="Used in (products using this ingredient)" onClick={() => handleUsedIn(ing)}>
                        <i className="fas fa-link"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-secondary mr-1" onClick={() => { setEditing(ing); setShowForm(true); }}>
                        <i className="fas fa-pen"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(ing)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <IngredientFormModal
          ingredient={editing}
          locations={locations}
          onClose={() => setShowForm(false)}
          onSaved={async () => { setShowForm(false); await load(); }}
        />
      )}
      {showPurchase && (
        <PurchaseModal
          ingredient={showPurchase}
          locations={locations}
          onClose={() => setShowPurchase(null)}
          onSaved={async () => { setShowPurchase(null); await load(); }}
        />
      )}
    </AdminLayout>
  );
};

// ── Create/edit an ingredient ───────────────────────────────────────────────
const IngredientFormModal = ({ ingredient, locations, onClose, onSaved }) => {
  const user = getAuth().currentUser;
  const [form, setForm] = useState({
    name: ingredient?.name || '',
    purchaseUnit: ingredient?.purchaseUnit || 'unit',
    useUnit: ingredient?.useUnit || 'unit',
    conversionFactor: ingredient?.conversionFactor ?? 1, // 1 purchase unit = N use units
    reorderPoint: ingredient?.reorderPoint ?? '',
    parLevel: ingredient?.parLevel ?? '',
  });
  const [saving, setSaving] = useState(false);
  // Auto-conversion when the units are in the same family (both directions)
  const suggestion = convertFactor(form.purchaseUnit, form.useUnit);

  const save = async () => {
    if (!form.name.trim()) { Swal.fire('Missing', 'Name is required', 'warning'); return; }
    setSaving(true);
    try {
      const db = getFirestore();
      const ref = ingredient?.id
        ? doc(db, 'users', user.uid, 'ingredients', ingredient.id)
        : doc(collection(db, 'users', user.uid, 'ingredients'));
      await setDoc(ref, {
        name: form.name.trim(),
        purchaseUnit: form.purchaseUnit,
        useUnit: form.useUnit,
        conversionFactor: parseFloat(form.conversionFactor) || 1,
        reorderPoint: form.reorderPoint === '' ? null : parseFloat(form.reorderPoint),
        parLevel: form.parLevel === '' ? null : parseFloat(form.parLevel),
        active: true,
        updatedAt: serverTimestamp(),
        ...(ingredient?.id ? {} : { createdAt: serverTimestamp(), stockByLocation: {}, costPerUseUnit: 0 }),
      }, { merge: true });
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  const unitSelect = (field) => (
    <select
      className="form-control"
      value={form[field]}
      onChange={(e) => {
        const next = { ...form, [field]: e.target.value };
        // Auto-fill the factor when the new pair converts automatically
        const auto = convertFactor(next.purchaseUnit, next.useUnit);
        if (auto != null) next.conversionFactor = auto;
        setForm(next);
      }}>
      {Object.entries(UNIT_FAMILIES).map(([fam, units]) => (
        <optgroup key={fam} label={fam}>{units.map(u => <option key={u} value={u}>{u}</option>)}</optgroup>
      ))}
    </select>
  );

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ borderRadius: 16 }}>
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">{ingredient ? 'Edit' : 'New'} ingredient</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="font-weight-bold">Name *</label>
              <input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Tomate, Carne de res, Pan brioche" />
            </div>
            <div className="row">
              <div className="col-6 form-group">
                <label className="font-weight-bold">Purchase unit</label>
                {unitSelect('purchaseUnit')}
                <small className="text-muted">how you buy it</small>
              </div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Use unit (kitchen)</label>
                {unitSelect('useUnit')}
                <small className="text-muted">how recipes consume it</small>
              </div>
            </div>
            <div className="form-group">
              <label className="font-weight-bold">Conversion: 1 {form.purchaseUnit} = </label>
              <div className="input-group">
                <input type="number" min="0" step="0.0001" className="form-control" value={form.conversionFactor} onChange={(e) => setForm({ ...form, conversionFactor: e.target.value })} />
                <div className="input-group-append"><span className="input-group-text">{form.useUnit}</span></div>
              </div>
              {suggestion != null && form.purchaseUnit !== form.useUnit && (
                <button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={() => setForm({ ...form, conversionFactor: suggestion })}>
                  Use standard: 1 {form.purchaseUnit} = {suggestion} {form.useUnit}
                </button>
              )}
            </div>
            <div className="row">
              <div className="col-6 form-group">
                <label className="font-weight-bold">Reorder point</label>
                <input type="number" min="0" className="form-control" value={form.reorderPoint} onChange={(e) => setForm({ ...form, reorderPoint: e.target.value })} placeholder={`in ${form.useUnit}`} />
                <small className="text-muted">alert when stock ≤ this</small>
              </div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Par level</label>
                <input type="number" min="0" className="form-control" value={form.parLevel} onChange={(e) => setForm({ ...form, parLevel: e.target.value })} placeholder={`in ${form.useUnit}`} />
                <small className="text-muted">ideal stock for a period</small>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Receive stock (a purchase) — weighted-average costing ───────────────────
const PurchaseModal = ({ ingredient, locations, onClose, onSaved }) => {
  const user = getAuth().currentUser;
  const [qty, setQty] = useState('');
  const [price, setPrice] = useState('');
  const [locationId, setLocationId] = useState(locations[0]?.id || '');
  const [vendor, setVendor] = useState('');
  const [saving, setSaving] = useState(false);

  const factor = Number(ingredient.conversionFactor) || 1;
  const useUnits = (parseFloat(qty) || 0) * factor;
  const currentStock = Number(ingredient.stockByLocation?.[locationId] || 0);
  const currentCost = Number(ingredient.costPerUseUnit) || 0;
  const newUnitCost = useUnits > 0 ? (parseFloat(price) || 0) / useUnits : 0;
  // Weighted average: (oldStock×oldCost + purchase$ ) / (oldStock + newUnits)
  const newAvg = (currentStock + useUnits) > 0
    ? ((currentStock * currentCost) + (parseFloat(price) || 0)) / (currentStock + useUnits)
    : 0;

  const save = async () => {
    if ((parseFloat(qty) || 0) <= 0) { Swal.fire('Missing', 'Quantity is required', 'warning'); return; }
    if (!locationId) { Swal.fire('Missing', 'Pick the receiving location', 'warning'); return; }
    setSaving(true);
    try {
      const db = getFirestore();
      const newStock = Math.round((currentStock + useUnits) * 10000) / 10000;
      await setDoc(doc(db, 'users', user.uid, 'ingredients', ingredient.id), {
        stockByLocation: { [locationId]: newStock },
        costPerUseUnit: newAvg,
        lastPurchasePrice: parseFloat(price) || 0,
        lastPurchaseAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Movement log (ingredient entry)
      await setDoc(doc(collection(db, 'users', user.uid, 'inventoryMovements')), {
        kind: 'ingredient',
        refId: ingredient.id,
        name: ingredient.name,
        locationId,
        delta: useUnits,
        unit: ingredient.useUnit || 'unit',
        before: currentStock,
        after: newStock,
        reason: 'purchase',
        createdAt: serverTimestamp(),
      });

      // The purchase doubles as an EXPENSE entry (money out) when a price was given
      if ((parseFloat(price) || 0) > 0) {
        await setDoc(doc(collection(db, 'users', user.uid, 'expenses')), {
          type: 'ingredient_purchase',
          ingredientId: ingredient.id,
          ingredientName: ingredient.name,
          amount: parseFloat(price),
          qty: parseFloat(qty),
          unit: ingredient.purchaseUnit,
          locationId,
          vendor: vendor.trim() || null,
          date: serverTimestamp(),
          createdAt: serverTimestamp(),
        });
      }

      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ borderRadius: 16 }}>
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">Receive stock — {ingredient.name}</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="font-weight-bold">Location *</label>
              <select className="form-control" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                {locations.map(l => <option key={l.id} value={l.id}>{l.companyName || l.locationName}</option>)}
              </select>
              <small className="text-muted">stock lands where the purchase is received</small>
            </div>
            <div className="row">
              <div className="col-6 form-group">
                <label className="font-weight-bold">Qty purchased ({ingredient.purchaseUnit}) *</label>
                <input type="number" min="0" step="0.01" className="form-control" value={qty} onChange={(e) => setQty(e.target.value)} />
              </div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Total price paid</label>
                <div className="input-group">
                  <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                  <input type="number" min="0" step="0.01" className="form-control" value={price} onChange={(e) => setPrice(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="font-weight-bold">Vendor (optional)</label>
              <input className="form-control" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="e.g. Restaurant Depot" />
            </div>
            <div className="alert alert-light border small">
              = <strong>{useUnits.toLocaleString()} {ingredient.useUnit}</strong> into stock
              {(parseFloat(price) || 0) > 0 && <> · unit cost {money(newUnitCost)}/{ingredient.useUnit} · new weighted avg <strong>{money(newAvg)}</strong></>}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={save} disabled={saving}>
              {saving ? 'Receiving…' : 'Receive into stock'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IngredientsPage;
